import { type OrderStatus, STATUS_METADATA } from '~/types/orders.ts';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import type { RootState } from '~/redux/store.ts';
import { formatDateTime, formatOrderStatus, normalizeUserRole } from '~/utils';
import { CheckCircle, Clock } from 'lucide-react';

export function OrderStatusCard({
  orderStatus,
  deliveredDate,
}: {
  orderStatus: OrderStatus;
  deliveredDate?: string | null;
}) {
  const { t } = useTranslation('order');

  const rawRole = useSelector((state: RootState) => state.auth.user?.role);
  const role = normalizeUserRole(rawRole);
  const trackingMessage = STATUS_METADATA[orderStatus].message[role];
  const deliveredDateFormatted = deliveredDate
    ? formatDateTime(deliveredDate)
    : undefined;

  let statusWrapperClassNames = 'bg-warning-50 border-warning-200';
  let statusTitleClassName = 'text-warning-900';
  let statusMessageClassName = 'text-warning-700';
  let icon = <Clock size={18} className="text-warning-700" />;

  if (orderStatus === 'DELIVERED') {
    statusWrapperClassNames = 'bg-success-50 border-success-200';
    statusTitleClassName = 'text-success-900';
    statusMessageClassName = 'text-success-700';
    icon = <CheckCircle size={18} className="text-success-700" />;
  }

  return (
    <div className={`p-4 rounded-lg border ${statusWrapperClassNames}`}>
      <div className="flex items-start gap-2">
        {icon}
        <div>
          <p className={`text-sm font-medium mb-1 ${statusTitleClassName}`}>
            {t('order.status')}: {formatOrderStatus(orderStatus, role)}
          </p>
          <p className={`text-xs ${statusMessageClassName}`}>
            {t(trackingMessage, { deliveredDate: deliveredDateFormatted })}
          </p>
        </div>
      </div>
    </div>
  );
}
